"use client"

import { NewTabLink } from "./new-tab-link"
import { SaveStatus } from "./account"
import { HardenView } from "./harden-view"
import { AssignmentsView } from "./assignments-view"
import { openAssignments } from "@/lib/breach/assignments"
import { NowBar } from "./now-panel"
import { useMemo } from "react"
import Link from "next/link"
import { AlertTriangle, Info, Printer } from "lucide-react"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { derive, incidentTypeLabel } from "@/lib/breach/intake"
import { allApplicableItemIds, CONTENT_REVIEWED, type Step } from "@/lib/breach/steps"
import { CALL_US } from "@/lib/breach/help-links"
import { progress, type Workspace } from "@/lib/breach/workspace"
import { deleteWorkspace, saveWorkspace } from "@/lib/breach/storage"
import { SaveProgress } from "./save-progress"
import { ClearAll } from "./clear-all"
import { SignInNudge } from "./sign-in-nudge"
import type { GoTo } from "./incident-nav"
import { DeadlineColumn } from "./deadline-clocks"
import { IntroPages } from "./intro-pages"
import { IncidentIndex } from "./incident-index"
import { StepPager } from "./step-pager"
import { AnswersPage } from "./answers-page"
import { Contacts, Evidence, Summary, Timeline, type WorkspaceUpdater } from "./incident-tools"

export type ResolvedView =
  | { v: "intro"; p: 0 | 1 }
  | { v: "index" }
  | { v: "step"; step: Step; p: number }
  | { v: "answers" }
  | { v: "harden"; p: number }
  | { v: "timeline" }
  | { v: "evidence" }
  | { v: "contacts" }
  | { v: "assignments" }
  | { v: "summary" }

const NAV_ITEMS = [
  { v: "index", label: "Plan" },
  { v: "answers", label: "My answers" },
  { v: "timeline", label: "Timeline" },
  { v: "evidence", label: "Evidence" },
  { v: "contacts", label: "Contacts" },
  { v: "assignments", label: "Assignments" },
  { v: "summary", label: "Summary & export" },
  { v: "harden", label: "After the incident" },
] as const

/**
 * Persistent shell around every incident view: header (title, severity, overall
 * progress, warnings, print), the section nav, the main column, and the
 * deadline-clock column (above content on mobile, sticky right rail on lg+).
 */
export function IncidentShell({ w, update, persistOk, view, go, syncing = false, syncError = null }: {
  w: Workspace
  update: WorkspaceUpdater
  persistOk: boolean
  syncing?: boolean
  syncError?: string | null
  view: ResolvedView
  go: GoTo
}) {
  const derived = useMemo(() => derive(w.intake), [w.intake])
  const overall = useMemo(() => progress(w, allApplicableItemIds(w.intake)), [w])
  const currentNav: (typeof NAV_ITEMS)[number]["v"] =
    view.v === "intro" || view.v === "index" || view.v === "step" ? "index" : view.v

  const handleDelete = () => {
    if (confirm("Delete this incident from this browser? This cannot be undone.")) {
      deleteWorkspace(w.id)
      window.location.href = "/start"
    }
  }

  return (
    <div className="mx-auto max-w-6xl">
      {!CONTENT_REVIEWED && (
        <p className="mb-4 rounded-md border border-border bg-card p-3 text-sm text-muted-foreground" role="note">
          <Info className="mr-1 inline h-4 w-4" aria-hidden /> Draft content — still being reviewed by security and legal experts. Treat it as a starting checklist.
        </p>
      )}
      {!persistOk && (
        <p role="alert" className="mb-4 rounded-md border border-destructive/60 bg-destructive/10 p-3 text-sm">
          <AlertTriangle className="mr-1 inline h-4 w-4" aria-hidden /> This browser isn&apos;t saving your progress (private mode or blocked storage). Use <strong>Summary &amp; export</strong> to keep a copy.
        </p>
      )}

      <header className="mb-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-2xl font-bold sm:text-3xl">{incidentTypeLabel(w.intake.incidentType)}</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Severity guide: <strong className={cn(derived.severity === "critical" && "text-destructive")}>{({ critical: "Critical", high: "High", moderate: "Moderate", unclear: "Not enough information yet" } as const)[derived.severity]}</strong>
              {" · "}{overall.done}/{overall.total} actions done
              {w.intake.noticedAt && <> · first noticed {new Date(w.intake.noticedAt).toLocaleString()}</>}
            </p>
            <p className="mt-1 text-xs text-muted-foreground"><SaveStatus syncing={syncing} syncError={syncError} /></p>
            <SaveProgress className="mt-2" save={() => saveWorkspace(w)} savedAt={persistOk ? w.updatedAt : undefined} />
          </div>
          <div className="flex gap-2 print:hidden">
            <Button variant="outline" onClick={() => window.print()}><Printer /> Print</Button>
            <ClearAll size="default" />
            <NewTabLink href={CALL_US.href} className="inline-flex items-center gap-1 self-center text-sm text-muted-foreground underline hover:text-foreground">
              Need a person?
            </NewTabLink>
          </div>
        </div>

        <nav aria-label="Incident sections" className="mt-4 flex flex-wrap gap-x-4 gap-y-1 border-b border-border print:hidden">
          {NAV_ITEMS.map((item) => (
            <button key={item.v} type="button"
              aria-current={currentNav === item.v ? "page" : undefined}
              onClick={() => go({ v: item.v })}
              className={cn(
                "-mb-px border-b-2 px-1 py-2 text-sm font-medium focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-solid",
                currentNav === item.v
                  ? "border-accent-solid text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground",
              )}>
              {item.label}
              {item.v === "assignments" && openAssignments(w).length > 0 && <span className="ml-1.5 rounded-full bg-accent-solid px-1.5 py-0.5 text-xs text-accent-solid-foreground">{openAssignments(w).length}</span>}
            </button>
          ))}
        </nav>
      </header>

      <SignInNudge className="mb-6" next={typeof window !== "undefined" ? window.location.pathname + window.location.search : "/incident"} />

      <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_320px] lg:items-start lg:gap-8">
        <DeadlineColumn w={w} update={update} go={go} />
        <div className="min-w-0 lg:col-start-1 lg:row-start-1">
          {view.v !== "intro" && view.v !== "index" && <NowBar w={w} go={go} />}
          {view.v === "intro" && <IntroPages update={update} page={view.p} go={go} />}
          {view.v === "index" && <IncidentIndex w={w} update={update} go={go} />}
          {view.v === "step" && <StepPager w={w} update={update} step={view.step} page={view.p} go={go} />}
          {view.v === "answers" && <AnswersPage w={w} update={update} />}
          {view.v === "harden" && <HardenView w={w} update={update} page={view.p} go={go} />}
          {view.v === "timeline" && <Timeline w={w} update={update} />}
          {view.v === "evidence" && <Evidence w={w} update={update} />}
          {view.v === "contacts" && <Contacts w={w} update={update} go={go} />}
          {view.v === "assignments" && <AssignmentsView w={w} update={update} go={go} />}
          {view.v === "summary" && <Summary w={w} onDelete={handleDelete} />}
        </div>
      </div>
    </div>
  )
}
